/**
 * TCF Canada player helpers shared by the web and the mobile app (THE BRIDGE WORLD Academy).
 * The server stays the reference (it counts the words it keeps); these only drive the live display.
 */

/** Keys of the accent row under the EE text areas (AZERTY training, référentiel §5). */
export const TCF_ACCENT_KEYS = [
  "é", "è", "ê", "ë", "à", "â", "ç", "ù", "û", "ô", "î", "ï", "œ", "É", "À", "Ç", "«", "»",
] as const;

/**
 * Word count as the exam counts it, same rule as the server (engine.words): groups separated by
 * spaces that contain at least one letter or digit (« aujourd'hui » = 1, a lone « – » = 0).
 */
export function countWords(text: string): number {
  return text
    .replace(/\u00a0/g, " ")
    .split(/\s+/)
    .filter((word) => /[\p{L}\p{N}]/u.test(word)).length;
}

/** Inserts `insert` at the selection (or replaces it); returns the new text and caret position. */
export function insertAtSelection(
  text: string,
  insert: string,
  selection: { start: number; end: number } = { start: text.length, end: text.length },
): { text: string; caret: number } {
  const start = Math.max(0, Math.min(selection.start, text.length));
  const end = Math.max(start, Math.min(selection.end, text.length));
  return { text: text.slice(0, start) + insert + text.slice(end), caret: start + insert.length };
}

/** Seconds left on a server deadline, `elapsedMs` after the server said `remainingSeconds`. */
export function secondsLeft(remainingSeconds: number, elapsedMs: number): number {
  return Math.max(0, Math.ceil(remainingSeconds - elapsedMs / 1000));
}

/** « 34:05 » (or « 1:02:05 ») for the exam timer. */
export function formatClock(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = String(s % 60).padStart(2, "0");
  return hours ? `${hours}:${String(minutes).padStart(2, "0")}:${seconds}` : `${minutes}:${seconds}`;
}

/**
 * XP earned by a daily challenge, same rule as the server (progress.submit_challenge): the
 * challenge's XP in proportion of the right answers, rounded down.
 */
export function challengeXp(xp: number, score: number, total: number): number {
  if (xp <= 0 || score <= 0) return 0;
  return Math.floor((xp * score) / Math.max(total, 1));
}

/** Speaking time of the EO section: preparation + speaking of every task, in seconds. */
export function speakingSeconds(
  tasks: Record<string, { prep_seconds?: number; seconds?: number }>,
): number {
  return Object.values(tasks).reduce(
    (sum, task) => sum + Math.max(0, task.prep_seconds ?? 0) + Math.max(0, task.seconds ?? 0),
    0,
  );
}

/** Index of the first unanswered question (free CE navigation), or 0 when all are answered. */
export function firstUnanswered(choices: ReadonlyArray<number | null | undefined>): number {
  const index = choices.findIndex((choice) => choice === null || choice === undefined);
  return index < 0 ? 0 : index;
}
